import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  getRequiresHubs,
  hasDirectControl,
  getDirectControlMethods,
} from "../../lib/specsUtils";

export default function HubRecommendations({
  allSelectedProducts = [],
  singleHubCandidates = [],
  twoHubCombos = [],
  softSingleHubRanking = [],
  selectedHubIds = new Set(),
  onToggleHub = () => {},
  fmtFt = (n) => (typeof n === "number" ? n.toLocaleString("hu-HU") + " Ft" : ""),
  onPrev = () => {},
  onProceed,
  showNav = true, // ⬅️ ÚJ: belső navigáció opcionális
}) {
  const navigate = useNavigate();

  const selectedSet =
    selectedHubIds instanceof Set ? selectedHubIds : new Set(selectedHubIds || []);

  const mandatoryProducts = useMemo(
    () => allSelectedProducts.filter((p) => (getRequiresHubs(p) || []).length > 0),
    [allSelectedProducts]
  );

  const canGoAppOnly = useMemo(() => {
    if (allSelectedProducts.length === 0) return false;
    return allSelectedProducts.every((p) => hasDirectControl(p));
  }, [allSelectedProducts]);

  const appOnlyMayBeLimited = useMemo(() => {
    if (!canGoAppOnly) return false;
    return allSelectedProducts.some((p) => (getRequiresHubs(p) || []).length > 0);
  }, [allSelectedProducts, canGoAppOnly]);

  const directControlApps = useMemo(() => {
    if (!canGoAppOnly) return [];
    const pairKey = (m) =>
      `${(m.app || "").trim().toLowerCase()}|${(m.protocol || "").trim().toLowerCase()}`;
    const seen = new Set();
    const out = [];
    for (const p of allSelectedProducts) {
      const methods = getDirectControlMethods(p);
      for (const m of methods) {
        const key = pairKey(m);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push({ app: m.app, protocol: m.protocol });
      }
    }
    return out;
  }, [allSelectedProducts, canGoAppOnly]);

  const showOneHub = Array.isArray(singleHubCandidates) && singleHubCandidates.length > 0;
  const showTwoHubCombos =
    !showOneHub && Array.isArray(twoHubCombos) && twoHubCombos.length > 0;
  const showSoftRanking =
    !showOneHub &&
    !showTwoHubCombos &&
    Array.isArray(softSingleHubRanking) &&
    softSingleHubRanking.length > 0;

  const handleProceed = () => {
    const ids = Array.from(selectedSet);
    if (typeof onProceed === "function") {
      onProceed(ids);
    } else {
      navigate("/builder/osszegzes", { state: { selectedHubIds: ids } });
    }
  };

  const handleProceedAppOnly = () => {
    if (typeof onProceed === "function") {
      onProceed([]);
    } else {
      navigate("/builder/osszegzes", { state: { selectedHubIds: [] } });
    }
  };

  return (
    <section className="space-y-8">
      <header>
        <h2 className="text-2xl font-semibold mb-2">HUB ajánlás</h2>
        <p className="text-sm text-gray-600">
          {mandatoryProducts.length > 0
            ? "Az alábbi központ(ok) lefedik a kötelező kompatibilitási igényeket."
            : "Nincs kötelező központ; az alábbi lehetőségek közül választhatsz, vagy használhatod az eszközöket közvetlenül is (appal)."}
        </p>
      </header>

      {canGoAppOnly && (
        <motion.div
          className="bg-white rounded-2xl border border-emerald-300 ring-1 ring-emerald-200 p-4"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-emerald-800">
                Használat központ nélkül (appal)
              </h3>
              <p className="text-sm text-gray-700">
                A jelenlegi választás központ nélkül is használható: a termékek
                a gyártói alkalmazásokon keresztül vezérelhetők Wi-Fi/Bluetooth/Thread kapcsolattal.
              </p>

              {appOnlyMayBeLimited && (
                <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                  Figyelem: egyes funkciók (pl. bonyolult automatizációk, jelenetek, többplatformos
                  integráció) csak központtal érhetők el teljes körűen.
                </p>
              )}

              {directControlApps.length > 0 && (
                <ul className="mt-3 text-sm text-gray-700 list-disc pl-5">
                  {directControlApps.map((m, i) => (
                    <li key={`${m.app}-${m.protocol}-${i}`}>
                      App: <span className="font-medium">{m.app}</span>{" "}
                      <span className="text-gray-500">({m.protocol})</span>
                    </li>
                  ))}
                </ul>
              )}

              <p className="text-xs text-gray-500 mt-2">
                Tipp: ellenőrizd, hogy az eszközök ugyanarra a Wi-Fi hálózatra
                csatlakoznak-e, és szükség esetén legyen létrehozva fiók az adott appban.
              </p>
            </div>

            <div className="shrink-0">
              <button
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
                onClick={handleProceedAppOnly}
              >
                Folytatás központ nélkül
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {showOneHub && (
        <div>
          <h3 className="text-xl font-semibold mb-3">1 HUB-bal megoldható</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {singleHubCandidates.map((h, idx) => {
              const img =
                (Array.isArray(h.images) && h.images[0]) || h.image || "/img/placeholder.webp";
              const active = selectedSet.has(h.id);
              return (
                <motion.button
                  key={h.id}
                  onClick={() => onToggleHub(h.id)}
                  className={`text-left bg-white rounded-2xl border ${
                    active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200"
                  } hover:border-gray-300 shadow-sm hover:shadow-md transition-all p-4`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.02 * idx, duration: 0.2 }}
                >
                  <div className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-gray-50 mb-3">
                    <img src={img} alt={h.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="font-semibold">{h.name}</div>
                  <div className="text-sm text-gray-600 line-clamp-2">{h.description}</div>
                  <div className="text-sm font-medium mt-1">{fmtFt(h.price)}</div>
                  {active && <div className="mt-2 text-xs text-blue-600">Kiválasztva</div>}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      {!showOneHub && showTwoHubCombos && (
        <div>
          <h3 className="text-xl font-semibold mb-3">2 HUB-os kombinációk</h3>
          <div className="space-y-4">
            {twoHubCombos.slice(0, 8).map((combo, idx) => (
              <motion.div
                key={idx}
                className="bg-white rounded-xl border border-gray-200 p-4"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  {combo.hubs.map((h) => {
                    const img =
                      (Array.isArray(h.images) && h.images[0]) || h.image || "/img/placeholder.webp";
                    const active = selectedSet.has(h.id);
                    return (
                      <button
                        key={h.id}
                        onClick={() => onToggleHub(h.id)}
                        className={`flex items-center gap-3 flex-1 w-full border rounded-lg p-3 transition ${
                          active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200"
                        }`}
                      >
                        <div className="w-24 h-20 rounded bg-gray-50 border overflow-hidden">
                          <img src={img} alt={h.name} className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium">{h.name}</div>
                          <div className="text-xs text-gray-600 line-clamp-2">{h.description}</div>
                        </div>
                        <div className="text-sm font-medium">{fmtFt(h.price)}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 text-sm text-gray-700">
                  Összár (kb.): {fmtFt(combo.approxPrice)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {!showOneHub && !showTwoHubCombos && showSoftRanking && (
        <div>
          <h3 className="text-xl font-semibold mb-3">Javasolt központok</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {softSingleHubRanking.map(({ hub, covered, score }, idx) => {
              const img =
                (Array.isArray(hub.images) && hub.images[0]) || hub.image || "/img/placeholder.webp";
              const active = selectedSet.has(hub.id);
              return (
                <motion.button
                  key={hub.id}
                  onClick={() => onToggleHub(hub.id)}
                  className={`text-left bg-white rounded-2xl border ${
                    active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200"
                  } hover:border-gray-300 shadow-sm hover:shadow-md transition-all p-4`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.02 * idx, duration: 0.2 }}
                >
                  <div className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-gray-50 mb-3">
                    <img src={img} alt={hub.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="font-semibold">{hub.name}</div>
                  <div className="text-sm text-gray-600 line-clamp-2">{hub.description}</div>
                  <div className="flex items-center gap-2 text-xs my-2">
                    <span className="px-2 py-0.5 rounded bg-blue-50 border border-blue-200">
                      Illeszkedő termékek: {covered}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-gray-50 border">Pont: {score}</span>
                  </div>
                  <div className="text-sm font-medium">{fmtFt(hub.price)}</div>
                  {active && <div className="mt-2 text-xs text-blue-600">Kiválasztva</div>}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      {!showOneHub && !showTwoHubCombos && !showSoftRanking && !canGoAppOnly && (
        <p className="text-gray-600 text-sm">
          Nem találtunk megfelelő központot a jelenlegi választásodhoz.
          Próbálj meg kevesebb, vagy más termékeket választani, vagy jelezd nekünk!
        </p>
      )}

      {/* BELSŐ NAV – csak ha szeretnénk megjeleníteni */}
      {showNav && (
        <div className="flex justify-between mt-8">
          <button className="border px-5 py-2 rounded hover:bg-gray-50" onClick={onPrev}>
            Vissza
          </button>
          <button
            className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={selectedSet.size === 0}
            onClick={handleProceed}
          >
            Tovább
          </button>
        </div>
      )}
    </section>
  );
}
